import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, X, Shield, ArrowRight, AlertTriangle } from "lucide-react";

const PLANS = [
  {
    id: "STARTER", name: "Starter", price: 29, label: "See the Truth",
    color: "emerald", borderColor: "#10B981",
    desc: "Understand where your money actually goes.",
    features: [
      { name: "Upload financial documents", included: true },
      { name: "Basic vault visibility", included: true },
      { name: "View obligation breakdown", included: true },
      { name: "Limited AI classification", included: true },
      { name: "Read-only vault view", included: true },
      { name: "Simulations", included: false },
      { name: "Financial guardrails", included: false },
      { name: "AI coaching", included: false },
      { name: "Bank connections", included: false },
    ],
    cta: "Start Free Trial", popular: false,
  },
  {
    id: "GROWTH", name: "Growth", price: 99, label: "Take Control",
    color: "cyan", borderColor: "#06B6D4",
    desc: "Actively manage your financial reality.",
    features: [
      { name: "Bank connections", included: true },
      { name: "Statement uploads", included: true },
      { name: "Full vault allocation (6 vaults)", included: true },
      { name: "Core truth reporting", included: true },
      { name: "Financial obligation visibility", included: true },
      { name: "Guardrails (advisory + confirm)", included: true },
      { name: "AI coaching (basic)", included: true },
      { name: "Simulations", included: true },
      { name: "Approval workflows", included: false },
      { name: "Hard-block guardrails", included: false },
    ],
    cta: "Upgrade to Growth", popular: true,
  },
  {
    id: "PRO", name: "Pro", price: 249, label: "Stay Protected",
    color: "amber", borderColor: "#F59E0B",
    desc: "Never miss a financial obligation again.",
    features: [
      { name: "Everything in Growth", included: true },
      { name: "Approval workflows", included: true },
      { name: "Hard-block guardrails", included: true },
      { name: "Advanced simulations", included: true },
      { name: "Full Behaviour Intelligence", included: true },
      { name: "Accountant collaboration", included: true },
      { name: "Audit exports", included: true },
      { name: "Multi-entity support", included: true },
    ],
    cta: "Go Pro", popular: false,
  },
];

export default function PricingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020617] text-slate-50" data-testid="pricing-page">
      {/* Nav */}
      <nav className="h-16 flex items-center justify-between px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="" className="w-8 h-8" />
          <span className="font-barlow font-bold text-lg">SENTIENT VAULT</span>
        </div>
        <Button variant="outline" className="border-slate-700 text-sm" onClick={() => navigate("/login")}>Sign In</Button>
      </nav>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="font-barlow font-bold text-4xl md:text-5xl lg:text-6xl text-slate-50 mb-4">
            Stop spending money<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-500">that isn't yours.</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto font-manrope">
            Most tools track transactions. Sentient Vault <strong className="text-slate-200">helps protect your business from failing its financial obligations.</strong>
          </p>
        </div>

        {/* Non-custody trust statement */}
        <div
          data-testid="non-custody-trust-card"
          className="max-w-3xl mx-auto mb-16 rounded-2xl border border-slate-800/60 bg-slate-900/40 p-5 sm:p-6"
        >
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <div className="space-y-1.5">
              <div className="text-sm font-semibold text-slate-200 font-manrope">
                Sentient Vault does not custody or control your money.
              </div>
              <p className="text-sm text-slate-400 font-manrope leading-relaxed">
                The platform analyses financial records, obligations,
                liabilities, and business commitments to help companies
                avoid hidden financial risk. We are not a bank, we are
                not a payment processor, and we do not hold funds.
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-6 flex flex-col ${
                plan.popular
                  ? 'border-cyan-500/40 bg-cyan-500/[0.03] shadow-[0_0_40px_rgba(6,182,212,0.08)]'
                  : 'border-slate-800/50 bg-slate-900/30'
              }`}
              data-testid={`plan-card-${plan.id.toLowerCase()}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-slate-900 text-[10px] font-bold px-3 py-1 border-none">
                  MOST POPULAR
                </Badge>
              )}
              <div className="mb-4">
                <span className="text-xs uppercase tracking-[0.2em] font-mono" style={{ color: plan.borderColor }}>{plan.label}</span>
                <h3 className="font-barlow font-bold text-2xl text-slate-50 mt-1">{plan.name}</h3>
                <p className="text-sm text-slate-500 mt-1 font-manrope">{plan.desc}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="font-mono text-4xl font-bold text-slate-50">£{plan.price}</span>
                <span className="text-sm text-slate-500">/month</span>
              </div>
              <div className="flex-1 space-y-2.5 mb-6">
                {plan.features.map((f) => (
                  <div key={f.name} className="flex items-center gap-2.5">
                    {f.included ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-700 shrink-0" />
                    )}
                    <span className={`text-sm ${f.included ? 'text-slate-300' : 'text-slate-600'}`}>{f.name}</span>
                  </div>
                ))}
              </div>
              <Button
                className={`w-full h-11 rounded-xl font-semibold ${
                  plan.popular
                    ? 'bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-400 hover:to-cyan-500 text-slate-900'
                    : plan.id === "PRO"
                    ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-900'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}
                onClick={() => navigate("/register")}
                data-testid={`plan-cta-${plan.id.toLowerCase()}`}
              >
                {plan.cta} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          ))}
        </div>

        {/* Accountant + Enterprise */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-20">
          <div className="rounded-2xl border border-slate-800/50 bg-slate-900/20 p-6">
            <span className="text-xs text-amber-400 font-mono uppercase tracking-wider">For Accountants</span>
            <h3 className="font-barlow font-bold text-xl text-slate-50 mt-2">Accountant Plan</h3>
            <p className="text-sm text-slate-500 mt-1 font-manrope">Manage multiple clients with full financial oversight.</p>
            <div className="mt-3 text-sm text-slate-400">From <span className="font-mono text-lg text-slate-200">£99</span>/month</div>
            <Button variant="outline" className="mt-4 border-amber-500/30 text-amber-400" onClick={() => navigate("/register")}>
              Book a Demo <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
          <div className="rounded-2xl border border-slate-800/50 bg-slate-900/20 p-6">
            <span className="text-xs text-slate-500 font-mono uppercase tracking-wider">Enterprise</span>
            <h3 className="font-barlow font-bold text-xl text-slate-50 mt-2">Embed Intelligence</h3>
            <p className="text-sm text-slate-500 mt-1 font-manrope">Integrate Sentient Vault into your financial ecosystem.</p>
            <div className="mt-3 text-sm text-slate-400"><span className="font-mono text-lg text-slate-200">Custom</span> pricing</div>
            <Button variant="outline" className="mt-4 border-slate-700 text-slate-400">
              Contact Us <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </div>

        {/* Psychological Section */}
        <div className="text-center mb-16 max-w-2xl mx-auto">
          <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-4" />
          <h2 className="font-barlow font-bold text-2xl md:text-3xl text-slate-50 mb-4">What happens without Sentient Vault?</h2>
          <div className="space-y-2 text-sm text-slate-400 font-manrope">
            <p>VAT gets spent accidentally.</p>
            <p>Supplier payments get delayed.</p>
            <p>Cashflow becomes unpredictable.</p>
            <p>Profit becomes an illusion.</p>
          </div>
          <p className="mt-6 text-lg text-amber-400 font-barlow font-semibold">Sentient Vault prevents this — automatically.</p>
          <Button className="mt-6 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold px-8 h-11 rounded-xl" onClick={() => navigate("/register")} data-testid="pricing-cta-bottom">
            Start Your Free Trial <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800/30 py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4 text-amber-400/40" />
              <span className="text-xs text-slate-600">Sentient Vault™ — Obligation-Enforced Finance</span>
            </div>
            <a href="mailto:invitation@celestialmindsfze.com" className="text-[10px] text-amber-400/60 hover:text-amber-300 transition-colors">Enquiries: invitation@celestialmindsfze.com</a>
          </div>
          <div className="text-center text-[9px] text-slate-700">A Celestial Minds FZE initiative. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
